import proj4 from 'proj4';

import type { Coordinate, MatcherList } from './types';
import { GEO_LINK_CLASS_NAME } from './types';

function geoLink(options: Coordinate): string {
  return `<a class="${GEO_LINK_CLASS_NAME} cursor-pointer" data-latitude="${options.latitude}" data-longitude="${options.longitude}">${options.description}</a>`;
}

function parseCoordinateValue(value: string): number {
  return Number.parseFloat(value.replace(',', '.'));
}

function parseCoordinateWithHemisphere(
  value: string,
  hemisphere: string | undefined,
  positiveHemisphere: string,
): number {
  const parsedValue = parseCoordinateValue(value);

  if (!hemisphere) {
    return parsedValue;
  }

  const normalizedHemisphere = hemisphere.toLowerCase();
  return normalizedHemisphere === positiveHemisphere
    ? Math.abs(parsedValue)
    : -Math.abs(parsedValue);
}

const degSym = '[° ,]';
const minSym = '[′’\']';
const secSym = `([′″”"]|${minSym}{2})`;
const northing = '[ns]';
const easting = '[ew]';
const int2 = '\\d{1,2}';
const int3 = '\\d{1,3}';
const dec = '\\.\\d+';
const optSep = '[,:;]? ?';

export const matchers: MatcherList = {
  latLonCoordinate: {
    regex: /(-?\d{1,3}[.,]\d+)[,:; ]+(-?\d{1,3}[.,]\d+)/g,
    matchCallback: (
      description: string,
      latitude: string,
      longitude: string,
    ) => {
      return geoLink({
        description,
        latitude: parseCoordinateValue(latitude),
        longitude: parseCoordinateValue(longitude),
      });
    },
  },
  mgrsCoordinate: {
    // Matches compact MGRS values like 32VPL2665383299 (zone + band + 100km square + easting/northing).
    regex: /\b(?:[1-5]?\d|60)[c-hj-np-x][a-hj-np-z]{2}(?:\d\d){1,5}\b/gi,
    matchCallback(text: string) {
      const point = proj4.Point.fromMGRS(text);

      return geoLink({
        description: text,
        latitude: point.y,
        longitude: point.x,
      });
    },
  },
  utmCoordinate: {
    regex: /\b(\d{1,2})([a-hj-np-z])(?:,\s*|\s+)(\d+)(?:,\s*|\s+)(\d+)\b/gi,
    matchCallback(
      text: string,
      zone: string,
      band: string,
      eastings: string,
      northings: string,
    ) {
      const south
        = !'NPQRSTUVWX'.includes(band.toUpperCase()) ? ' +south' : ''; // if zone is not north of M => southern hemisphere
      const utm = `+proj=utm +ellps=WGS84 +datum=WGS84 +no_defs +zone=${zone}${south}`;
      const wgs84 = '+proj=longlat +ellps=WGS84 +datum=WGS84 +no_defs';
      const coords = proj4(utm, wgs84, [
        Number.parseInt(eastings, 10),
        Number.parseInt(northings, 10),
      ]);
      return geoLink({
        description: text,
        longitude: coords[0],
        latitude: coords[1],
      });
    },
  },
  webMercatorCoordinate: {
    regex: /\b(-?\d{6,}(?:[.,]\d+)?)([ew])?(?:,\s*|\s+)(-?\d{6,}(?:[.,]\d+)?)([ns])?\b/gi,
    matchCallback: (
      text: string,
      eastings: string,
      eastingHemisphere: string,
      northings: string,
      northingHemisphere: string,
    ) => {
      const parsedEastings = parseCoordinateWithHemisphere(
        eastings,
        eastingHemisphere,
        'e',
      );
      const parsedNorthings = parseCoordinateWithHemisphere(
        northings,
        northingHemisphere,
        'n',
      );
      const wgs84Coordinates = proj4('EPSG:3857', 'EPSG:4326', [
        parsedEastings,
        parsedNorthings,
      ]);

      return geoLink({
        description: text,
        longitude: wgs84Coordinates[0],
        latitude: wgs84Coordinates[1],
      });
    },
  },
  latLonDegMinSecCoordinate: {
    regex: new RegExp(
      [
        `(${int3})${optSep}${degSym}${optSep}`,
        `(${int2})${optSep}${minSym}${optSep}`,
        `(${int2})(${dec})?${optSep}${secSym}${optSep}(${northing})`,
        ',?\\s+',
        `(${int3})${optSep}${degSym}${optSep}`,
        `(${int2})${optSep}${minSym}${optSep}`,
        `(${int2})(${dec})?${optSep}${secSym}${optSep}(${easting})`,
      ].join(''),
      'gi',
    ),
    matchCallback(...args: string[]) {
      const text = args[0];
      const latDeg = Number.parseInt(args[1], 10);
      const latMin = Number.parseInt(args[2], 10);
      const latSecDec = Number.parseFloat(args[4]) || 0;
      const latSec = Number.parseInt(args[3], 10) + latSecDec;
      const lonDeg = Number.parseInt(args[7], 10);
      const lonMin = Number.parseInt(args[8], 10);
      const lonSecDec = Number.parseFloat(args[10]) || 0;
      const lonSec = Number.parseInt(args[9], 10) + lonSecDec;
      const north = args[6].toLowerCase() === 'n';
      const east = args[12].toLowerCase() === 'e';

      const latitude
        = (latDeg + latMin / 60 + latSec / 3600) * (north ? 1 : -1);
      const longitude
        = (lonDeg + lonMin / 60 + lonSec / 3600) * (east ? 1 : -1);

      return geoLink({
        description: text,
        latitude,
        longitude,
      });
    },
  },
  latLonDegDecimalMinCoordinate: {
    regex: new RegExp(
      [
        `(${int3})${optSep}${degSym}${optSep}`,
        `(${int2})(${dec})?${optSep}${minSym}${optSep}(${northing})`,
        ',?\\s+',
        `(${int3})${optSep}${degSym}${optSep}`,
        `(${int2})(${dec})?${optSep}${minSym}${optSep}(${easting})`,
      ].join(''),
      'gi',
    ),
    matchCallback(...args: string[]) {
      const text = args[0];
      const latDeg = Number.parseInt(args[1], 10);
      const latMinDec = Number.parseFloat(args[3]) || 0;
      const latMin = Number.parseInt(args[2], 10) + latMinDec;
      const lonDeg = Number.parseInt(args[5], 10);
      const lonMinDec = Number.parseFloat(args[7]) || 0;
      const lonMin = Number.parseInt(args[6], 10) + lonMinDec;
      const north = args[4].toLowerCase() === 'n';
      const east = args[8].toLowerCase() === 'e';

      const latitude = (latDeg + latMin / 60) * (north ? 1 : -1);
      const longitude = (lonDeg + lonMin / 60) * (east ? 1 : -1);

      return geoLink({
        description: text,
        latitude,
        longitude,
      });
    },
  },
  // Commented out for first iteration
  // mention: {
  //     regex: /\B@[a-zA-Z0-9_]{3,}/g,
  //     fn: mention => `<a class="mention">${mention}</a>`,
  // },
  // hashtag: {
  //     regex: /\B#[a-zA-Z0-9_]{3,}/g,
  //     fn: hashtag => `<a class="hashtag">${hashtag}</a>`,
  // },
};
